(() => {
  const selector = '[data-alessa-compact-header]';
  const compactClass = 'alessa-header--compact';
  const bodyCompactClass = 'has-alessa-compact-header';
  const collapseThreshold = 100;
  const expandThreshold = 40;

  class AlessaCompactHeader {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.header = wrapper.querySelector('[data-header-height]');
      this.collectionNav = document.querySelector('[data-collection-nav]');
      this.isCompact = wrapper.classList.contains(compactClass);
      this.pendingState = null;
      this.frame = null;
      this.expandedHeaderHeight = this.header?.offsetHeight || 0;
      this.expandedCollectionHeight = this.collectionNav?.offsetHeight || 0;

      this.queueUpdate = this.queueUpdate.bind(this);
      this.onInteractionChange = this.onInteractionChange.bind(this);
      this.onResize = this.onResize.bind(this);

      this.wrapper.dataset.alessaCompactHeaderReady = 'true';
      this.bindShopWineChooser();
      this.bindEvents();
      this.update(true);
    }

    bindShopWineChooser() {
      const chooser = this.wrapper.querySelector('.menu__item--editorial');
      const trigger = chooser?.querySelector(':scope > [data-top-link]');
      if (!chooser || !trigger || trigger.dataset.alessaChooserReady === 'true') return;

      trigger.dataset.alessaChooserReady = 'true';
      trigger.addEventListener('click', (event) => {
        if (!window.matchMedia('(min-width: 1000px)').matches) return;
        event.preventDefault();
        chooser.dispatchEvent(new CustomEvent('theme:disclosure:show', {bubbles: false}));
      });
    }

    bindEvents() {
      document.addEventListener('theme:scroll', this.queueUpdate);
      window.addEventListener('scroll', this.queueUpdate, {passive: true});
      document.addEventListener('focusin', this.onInteractionChange);
      document.addEventListener('focusout', this.onInteractionChange);
      document.addEventListener('toggle', this.onInteractionChange, true);
      document.addEventListener('theme:resize', this.onResize);
      window.addEventListener('resize', this.onResize, {passive: true});

      this.interactionObserver = new MutationObserver(this.onInteractionChange);
      this.interactionObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-scroll-locked'],
      });
      this.interactionObserver.observe(this.wrapper, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'open', 'aria-expanded'],
      });

      document.querySelectorAll('.drawer, mobile-menu').forEach((element) => {
        this.interactionObserver.observe(element, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'open', 'aria-expanded'],
        });
      });
    }

    onInteractionChange() {
      window.setTimeout(this.queueUpdate, 0);
    }

    onResize() {
      if (!this.isCompact) {
        this.expandedHeaderHeight = this.header?.offsetHeight || this.expandedHeaderHeight;
        this.expandedCollectionHeight = this.collectionNav?.offsetHeight || this.expandedCollectionHeight;
      }
      this.queueUpdate();
    }

    queueUpdate() {
      if (this.frame !== null) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    desiredState(position) {
      if (window.matchMedia('(max-width: 999px)').matches) return true;
      if (position >= collapseThreshold) return true;
      if (position <= expandThreshold) return false;
      return this.isCompact;
    }

    isInteracting() {
      const activeElement = document.activeElement;
      const focusedInside = Boolean(
        activeElement &&
          this.wrapper.contains(activeElement) &&
          (activeElement.matches(':focus-visible') || activeElement.matches('input, textarea, select'))
      );
      const headerOverlayOpen = Boolean(
        this.wrapper.matches('.meganav--visible') ||
          this.wrapper.querySelector('details[open], header-search-popdown.is-open, .drawer.is-open')
      );
      const drawerOpen = Boolean(document.querySelector('.drawer.is-open, mobile-menu .is-open'));
      const pageLocked = document.documentElement.hasAttribute('data-scroll-locked');

      return focusedInside || headerOverlayOpen || drawerOpen || pageLocked;
    }

    update(initial = false) {
      const nextState = this.desiredState(window.scrollY);

      if (!initial && this.isInteracting()) {
        this.pendingState = nextState;
        return;
      }

      if (this.pendingState !== null && !this.isInteracting()) {
        this.pendingState = null;
      }

      this.setCompact(nextState);
    }

    compactHeight() {
      if (window.matchMedia('(max-width: 749px)').matches) return 68;
      if (window.matchMedia('(max-width: 1099px)').matches) return 64;
      if (this.wrapper.classList.contains('js__show__mobile') || this.wrapper.dataset.headerStyle === 'drawer') return 64;
      return 72;
    }

    setLayoutVariables(compact) {
      const root = document.documentElement;

      if (compact) {
        root.style.setProperty('--header-height', `${this.compactHeight()}px`);
        root.style.setProperty('--header-sticky-height', `${this.compactHeight()}px`);
        if (this.collectionNav) root.style.setProperty('--collection-nav-height', '48px');
        return;
      }

      const headerHeight = this.expandedHeaderHeight || this.header?.offsetHeight || 0;
      root.style.setProperty('--header-height', `${headerHeight}px`);
      root.style.setProperty('--header-sticky-height', `${headerHeight}px`);
      if (this.collectionNav) {
        const collectionHeight = this.expandedCollectionHeight || this.collectionNav.offsetHeight;
        root.style.setProperty('--collection-nav-height', `${collectionHeight}px`);
      }
    }

    setCompact(nextState) {
      if (nextState === this.isCompact) {
        this.wrapper.setAttribute('data-alessa-compact-state', nextState ? 'compact' : 'expanded');
        this.setLayoutVariables(nextState);
        return;
      }

      if (nextState) {
        this.expandedHeaderHeight = this.header?.offsetHeight || this.expandedHeaderHeight;
        this.expandedCollectionHeight = this.collectionNav?.offsetHeight || this.expandedCollectionHeight;
      }

      this.isCompact = nextState;
      this.wrapper.classList.toggle(compactClass, nextState);
      document.body.classList.toggle(bodyCompactClass, nextState);
      this.wrapper.setAttribute('data-alessa-compact-state', nextState ? 'compact' : 'expanded');
      this.setLayoutVariables(nextState);

      if (!nextState) {
        window.setTimeout(() => {
          if (this.isCompact) return;
          this.expandedHeaderHeight = this.header?.offsetHeight || this.expandedHeaderHeight;
          this.expandedCollectionHeight = this.collectionNav?.offsetHeight || this.expandedCollectionHeight;
          this.setLayoutVariables(false);
        }, 220);
      }
    }
  }

  const init = (context = document) => {
    const wrappers = [];
    if (context.matches?.(selector)) wrappers.push(context);
    context.querySelectorAll?.(selector).forEach((wrapper) => wrappers.push(wrapper));

    wrappers.forEach((wrapper) => {
      if (wrapper.dataset.alessaCompactHeaderReady === 'true') return;
      wrapper.alessaCompactHeader = new AlessaCompactHeader(wrapper);
    });
  };

  const start = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
